/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getGlobalSearch, GlobalSearchTypes } from '../../../services/globalSearchEndpoint';
import useWorkspaceStore from '../../../store/workspace/workspaceStore';
import useStore from '../store';

export interface MentionOption {
  id: string;
  label: string;
  type: 'account' | 'contact';
  avatar_url?: string | null;
  domain?: string;
  external_id: string;
}

interface MentionState {
  isOpen: boolean;
  query: string;
  startPosition: number;
  options: MentionOption[];
  selectedIndex: number;
  loading: boolean;
}

interface UseMentionsProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  inputMessage: string;
  setInputMessage: (message: string) => void;
}

export const useMentions = ({ textAreaRef, inputMessage, setInputMessage }: UseMentionsProps) => {
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const setMentions = useStore((s) => s.setMentions);
  const mentions = useStore((s) => s.mentions);

  const [mentionState, setMentionState] = useState<MentionState>({
    isOpen: false,
    query: '',
    startPosition: -1,
    options: [],
    selectedIndex: 0,
    loading: false,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  // Detect @ character and extract query
  const detectMention = useCallback(() => {
    if (!textAreaRef.current) return;

    const cursorPosition = textAreaRef.current.selectionStart;
    const textBeforeCursor = inputMessage.slice(0, cursorPosition);

    // Find the last @ before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      // No @ found, close mention dropdown
      if (mentionState.isOpen) {
        setMentionState(prev => ({ ...prev, isOpen: false }));
      }
      return;
    }

    // Check if there's a space between @ and cursor (means mention was completed)
    const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
    if (textAfterAt.includes(' ')) {
      if (mentionState.isOpen) {
        setMentionState(prev => ({ ...prev, isOpen: false }));
      }
      return;
    }

    // Extract mention query
    const query = textAfterAt;

    setMentionState(prev => ({
      ...prev,
      isOpen: true,
      query,
      startPosition: lastAtIndex,
      selectedIndex: 0,
    }));
  }, [inputMessage, textAreaRef, mentionState.isOpen]);

  // Fetch mention options from API
  useEffect(() => {
    if (!mentionState.isOpen || !workspaceId) {
      return undefined;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchMentions = async () => {
      setMentionState(prev => ({ ...prev, loading: true }));

      try {
        abortControllerRef.current = new AbortController();

        const response = await getGlobalSearch({
          workspace_identifier: workspaceId,
          search_query: mentionState.query || ' ', // Space for empty query to get all
          limit: 10,
        });

        const results = response?.data?.results || {};
        const options: MentionOption[] = [];

        // Add accounts
        if (results.accounts) {
          results.accounts.forEach((account: any) => {
            options.push({
              id: `account-${account.external_id}`,
              label: account.identifier || account.name,
              type: 'account',
              avatar_url: account.avatar_url,
              domain: account.domain,
              external_id: account.external_id,
            });
          });
        }

        // Add contacts
        if (results.contacts) {
          results.contacts.forEach((contact: any) => {
            options.push({
              id: `contact-${contact.external_id}`,
              label: contact.identifier || contact.name,
              type: 'contact',
              avatar_url: contact.avatar_url,
              external_id: contact.external_id,
            });
          });
        }

        setMentionState(prev => ({
          ...prev,
          options,
          loading: false,
          selectedIndex: 0,
        }));
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching mentions:', error);
          setMentionState(prev => ({ ...prev, loading: false, options: [] }));
        }
      }
    };

    // Debounce API calls
    const timeoutId = setTimeout(() => {
      fetchMentions();
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [mentionState.isOpen, mentionState.query, workspaceId]);

  // Insert selected mention
  const insertMention = useCallback((option: MentionOption) => {
    if (!textAreaRef.current || mentionState.startPosition === -1) return;

    const currentCursorPos = textAreaRef.current.selectionStart;

    const before = inputMessage.slice(0, mentionState.startPosition);
    const after = inputMessage.slice(currentCursorPos);

    // Insert mention with special format
    const mentionText = `@${option.label}`;
    const newText = `${before}${mentionText} ${after}`;

    setInputMessage(newText);

    // Store mention metadata
    setMentions(prev => [
      ...prev,
      {
        label: option.label,
        external_id: option.external_id,
        type: option.type,
        domain: option.domain,
        avatar_url: option.avatar_url,
      }
    ]);

    // Close mention dropdown
    setMentionState({
      isOpen: false,
      query: '',
      startPosition: -1,
      options: [],
      selectedIndex: 0,
      loading: false,
    });

    // Set cursor position after mention using textarea API
    setTimeout(() => {
      if (textAreaRef.current) {
        const newCursorPos = before.length + mentionText.length + 1;
        textAreaRef.current.focus();
        textAreaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [inputMessage, mentionState.startPosition, setInputMessage, textAreaRef, setMentions]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!mentionState.isOpen || mentionState.options.length === 0) {
      return false;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setMentionState(prev => ({
          ...prev,
          selectedIndex: (prev.selectedIndex + 1) % prev.options.length,
        }));
        return true;

      case 'ArrowUp':
        e.preventDefault();
        setMentionState(prev => ({
          ...prev,
          selectedIndex: prev.selectedIndex === 0 ? prev.options.length - 1 : prev.selectedIndex - 1,
        }));
        return true;

      case 'Enter':
      case 'Tab':
        if (mentionState.options[mentionState.selectedIndex]) {
          e.preventDefault();
          insertMention(mentionState.options[mentionState.selectedIndex]);
          return true;
        }
        break;

      case 'Escape':
        e.preventDefault();
        setMentionState(prev => ({ ...prev, isOpen: false }));
        return true;

      default:
        break;
    }

    return false;
  }, [mentionState, insertMention]);

  // Close mention dropdown
  const closeMentions = useCallback(() => {
    setMentionState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Detect mention on input change
  useEffect(() => {
    detectMention();
  }, [inputMessage, detectMention]);

  return {
    mentionState,
    insertMention,
    handleKeyDown,
    closeMentions,
  };
};
